import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { Button, Space, Card, Descriptions } from 'antd';
import Datetime from 'vl-common/src/components/Datestamp';
import useUserNextAppointments from '@hooks/useUserNextAppointments';
import { useCancelledAppointments } from '@hooks/useCancelledAppointments';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useProtectionData, ProtectionStatus } from '@hooks/useProtectionData';
import useUser from '@vl-core/hooks/useUser';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ConfirmationModal, errorModal, ErrorModal } from 'vl-common/src/components/Modals';
import { useTranslation } from 'react-i18next';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useDPACheck } from '@hooks/useDPACheck';
import DocmanDiscussionFormItem from '@components/Clinicians/Session/Components/DocmanDiscussionFormItem';
import useCase from '@hooks/useCase';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import ManagePatientDocmanConsentModal from '@components/Clinicians/Docman/ManagePatientDocmanConsentModal';

const NoResponseButton = styled(Button)`
  color: var(--ant-warning-color);
  border-color: var(--ant-warning-color);
  background: transparent;
  &:hover,
  &:focus {
    color: var(--ant-warning-color-active);
    border-color: var(--ant-warning-color-active);
    background: transparent;
  }
`;

const DocmanSectionWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const DataProtection = () => {
  const { CLINDMCONSENT, DOCMAN } = useRuntimeConfig();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { user_guid } = useUser();
  const { push } = useRouter();
  const queryClient = useQueryClient();
  const [showUpdateDocmanConsentModal, setShowUpdateDocmanConsentModal] = useState(false);
  const [confirmFailedCheckModalVisible, setConfirmFailedCheckModalVisible] = useState(false);
  const [cancelAppointmentError, setCancelAppointmentError] = useState(false);
  const [, invalidateAppointments] = useUserNextAppointments(10);
  const { setCancelledAppointments } = useCancelledAppointments();
  const { protectionData, refetchProtectionData } = useProtectionData();
  const [, setIsSavingDataProtection] = useSessionFormState('isSavingDataProtection', 0);
  const { isDPACheckCR } = useDPACheck();
  const {
    appointment: { appointment_id, appointment_completed, spec_cat_code, case_id, patient_user_guid }
  } = useSessionAppointment();

  const [caseDetails] = useCase(case_id);

  const { data: patientGPDetails } = useQuery({
    queryKey: ['getPatientGPDetails', patient_user_guid],
    queryFn: () => dispatch(actions.getGpDetails(patient_user_guid)),
    enabled: !!patient_user_guid
  });

  const hasPatientGP = !!patientGPDetails?.practice_name;
  const shouldShowDocmanManagement =
    DOCMAN && caseDetails?.docman_active && !caseDetails?.docman_consent && hasPatientGP;
  const showManageConsent = CLINDMCONSENT;

  // When it's non-MSK case review and DPA check is required, then show No response button
  const showNoResponseButton = isDPACheckCR && spec_cat_code !== 'MSK';

  const clearClinicianAppointments = useCallback(
    () => queryClient?.invalidateQueries({ queryKey: ['clinicianAppointment'] }),
    [queryClient]
  );

  const { mutate: handlePass } = useMutation({
    mutationFn: async (value: number) => {
      setIsSavingDataProtection((prev) => (prev ? prev + 1 : 1));
      const response = await dispatch(
        actions.postDataProtection({
          user_guid,
          appt_id: appointment_id,
          sec_conf: value
        })
      );

      if (response.type === 'POST_DATA_PROTECTION_FAIL') {
        errorModal({
          title: 'Error while completing DPA check',
          content: response.error.message
        });
      }
    },
    onSettled: (_, _1, value) => {
      setIsSavingDataProtection((prev) => (prev ? prev - 1 : 0));
      // Don't reload data protection if the security check failed since we are going to navigate
      // away from the session page anyway (and can cause bad requests against a cancelled appointment).
      if (value !== ProtectionStatus.Failed) {
        refetchProtectionData().then();
      }
    }
  });

  const Status: Record<number, string> = {
    [ProtectionStatus.Failed]: 'Fail',
    [ProtectionStatus.NoResponse]: 'No response',
    [ProtectionStatus.Recognised]: 'Skipped',
    [ProtectionStatus.Succeeded]: 'Succeeded'
  };

  const onCancelAppointment = async () => {
    await handlePass(ProtectionStatus.Failed);
    const cancelReason = {
      appt_id: appointment_id,
      user_id: user_guid,
      reason: 'DPA_NC'
    };
    const reasonResponse = await dispatch(actions.rescheduleCancel(cancelReason));
    if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) {
      invalidateAppointments();
      clearClinicianAppointments();
      setCancelledAppointments((cancelled) => ({ ...cancelled, [appointment_id]: true }));
      await push('/clinician/appointments');
    } else {
      setCancelAppointmentError(true);
    }
    setConfirmFailedCheckModalVisible(false);
  };

  const openManageConsentModal = () => setShowUpdateDocmanConsentModal(true);
  const closeManageConsentModal = () => setShowUpdateDocmanConsentModal(false);

  if (!protectionData) return null;

  return (
    <>
      <Card>
        <Descriptions
          title={`Please speak to the patient and confirm any ${showNoResponseButton ? 'FOUR' : 'THREE'} of the following....`}
        >
          <Descriptions.Item label={t('Name')}>{protectionData.name}</Descriptions.Item>
          <Descriptions.Item label={t('DateOfBirth')}>
            <Datetime date={protectionData.dob} format="Date only (localised) - 03/02/2022" />
          </Descriptions.Item>
          <Descriptions.Item label={t('Postcode')}>{protectionData.postcode}</Descriptions.Item>
          <Descriptions.Item label={t('Email')}>{protectionData.email}</Descriptions.Item>
          <Descriptions.Item label={t('Mobile')}>{protectionData.tel_mobile_no}</Descriptions.Item>
          <Descriptions.Item label={t('Alternate')}>{protectionData.tel_other_no}</Descriptions.Item>
        </Descriptions>
        <Space
          css={css`
            margin-top: 1em;
          `}
        >
          <Button
            type="primary"
            shape="round"
            size="large"
            danger
            ghost
            onClick={() => setConfirmFailedCheckModalVisible(true)}
            data-testid="failed-security-check"
            disabled={appointment_completed}
          >
            {t('Failed')}
          </Button>
          {showNoResponseButton && (
            <NoResponseButton
              type="primary"
              shape="round"
              size="large"
              className="ant-btn-warning"
              ghost
              onClick={() => handlePass(ProtectionStatus.NoResponse)}
              data-testid="no-response-security-check"
              disabled={appointment_completed}
            >
              {t('No response')}
            </NoResponseButton>
          )}
          <Button
            type="primary"
            shape="round"
            size="large"
            ghost
            onClick={() => handlePass(ProtectionStatus.Recognised)}
            data-testid="skipped-security-check"
            disabled={appointment_completed}
          >
            {t('Recognised')}
          </Button>
          <Button
            type="primary"
            shape="round"
            size="large"
            onClick={() => handlePass(ProtectionStatus.Succeeded)}
            data-testid="succeeded-security-check"
            disabled={appointment_completed}
          >
            {t('Succeeded')}
          </Button>
        </Space>
        <div
          css={css`
            padding-top: 1em;
          `}
        >
          {t("Please only use the recognised button if you are confident you know the patient'")}
        </div>
        {shouldShowDocmanManagement && (
          <DocmanSectionWrapper>
            <DocmanDiscussionFormItem
              css={css`
                margin: 0 1rem 0 0;
              `}
            />
            {showManageConsent && (
              <Button
                type="primary"
                onClick={openManageConsentModal}
                data-testid="manage-consent-button"
                disabled={appointment_completed}
              >
                {t('Manage Consent')}
              </Button>
            )}
          </DocmanSectionWrapper>
        )}
      </Card>

      {protectionData.dpa_check_by && (
        <Card title="Security check log">
          {t('Check ')}
          <strong>{Status[protectionData.dpa_check_state]}</strong> {t(' by')}{' '}
          <strong>{protectionData.dpa_check_by}</strong> {t(' on')}{' '}
          <strong>
            <Datetime date={protectionData.dpa_check_date} format="Date @ Time - 03/01/2022 @ 08:57" />
          </strong>
        </Card>
      )}

      <ConfirmationModal
        open={confirmFailedCheckModalVisible}
        okText="Confirm"
        cancelText="Cancel"
        onOk={onCancelAppointment}
        onCancel={() => setConfirmFailedCheckModalVisible(false)}
        title="Failed security check"
      >
        {t('failedDPACheck')}
      </ConfirmationModal>

      <ErrorModal
        open={cancelAppointmentError}
        onCancel={() => setCancelAppointmentError(false)}
        title="Failed to cancel appointment"
      >
        {t('There was an error cancelling the appointment. Please try again.')}
      </ErrorModal>

      <ManagePatientDocmanConsentModal
        key={Number(showUpdateDocmanConsentModal)}
        isOpen={showUpdateDocmanConsentModal}
        onCancel={closeManageConsentModal}
        caseId={case_id}
        patientId={patient_user_guid}
      />
    </>
  );
};

export default DataProtection;
